import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { IkWebPushPort, kWebPushClient, kWebPushKeyDone, kWebPushStepDone } from "../../../Admin/ReUsableTS/kWebPushClient";
import { AppAccountPost } from "./AppAccountPost";
import { AppArchiveStore } from "./AppArchiveStore";
import { AppPushRules } from "./AppPushRules";
import { AppMenuCheck } from "./AppMenuCheck";

// AppPush - "Notify me about new messages on this device" (owner 2026-11-01: "Push everywhere it works"). The settings
// menu item (a `menuitemcheckbox`, `AppMenuCheck`) turns this device's notifications on / off through the SHARED
// kWebPushClient; the server half is `/push` (`Bridge/src/PushRun`), the worker `wac-sw.js` (`AppServiceWorker`).
// ⭐ Offered only where the browser can receive pushes (`kWebPushClient.Supported`) - on an iPhone that is a site added
// to the Home Screen. ⛔ Turned on only by the owner's click (the browser asks only from a gesture).
// ⛔ A push carries the contact's name and "New message" - never a word of a message (`PushRules.Payload`).

/** kWebPushClient's port: `/push` on the service, with this browser's archive key. */
export class AppPushPort implements IkWebPushPort {
    constructor(private readonly ServiceUrl: string) { }

    /** DOCUMENTED EXCEPTION: one-line delegate - `Post` carries the guard and the handler. */
    public PublicKey = (Done: kWebPushKeyDone): void => this.Post("key", {}, new AppPushAnswer(Done, null).OnKey);

    /** DOCUMENTED EXCEPTION: one-line delegate - `Post` carries the guard and the handler. */
    public Store = (Sub: PushSubscriptionJSON, Done: kWebPushStepDone): void => this.Post("subscribe", { sub: Sub }, new AppPushAnswer(null, Done).OnStep);

    /** DOCUMENTED EXCEPTION: one-line delegate - `Post` carries the guard and the handler. */
    public Forget = (Endpoint: string, Done: kWebPushStepDone): void => this.Post("unsubscribe", { endpoint: Endpoint }, new AppPushAnswer(null, Done).OnStep);

    /** The one `/push` request: this browser's number + archive key, the action and its fields. */
    private Post = (Action: string, Extra: Record<string, unknown>, Done: (Status: number, Text: string) => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Body: string = AppPushRules.Body(AppArchiveStore.Number(), AppArchiveStore.Key(), Action, Extra);
            if (Body.length === 0 || this.ServiceUrl.length === 0) { Done(0, ""); return; }
            new AppAccountPost(this.ServiceUrl + AppPushRules.Route, Body, Done).Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPushPort.Post");
        }
    };
}

class AppPushAnswer {
    constructor(private readonly KeyDone: kWebPushKeyDone | null, private readonly StepDone: kWebPushStepDone | null) { }

    public OnKey = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const R = AppPushRules.Parse(Status, Text);
            if (this.KeyDone !== null) { this.KeyDone(R.Key, R.Why); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPushAnswer.OnKey");
        }
    };

    public OnStep = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const R = AppPushRules.Parse(Status, Text);
            if (this.StepDone !== null) { this.StepDone(R.Ok, R.Why); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPushAnswer.OnStep");
        }
    };
}

/** The browser half: one client, the menu item, its click. */
export class AppPush {
    public static readonly WorkerUrl: string = "wac-sw.js";
    private static Client: kWebPushClient | null = null;

    /** Registers the worker (a service is needed to subscribe) and listens for the item's click. */
    public static Install = (ServiceUrl: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (kWebPushClient.Supported() === false || nz(ServiceUrl).length === 0 || AppPush.Client !== null) { return; }
            AppPush.Client = new kWebPushClient(AppPush.WorkerUrl, new AppPushPort(ServiceUrl));
            AppPush.Client.Register();
            AppMenuCheck.Listen(AppPush.OnClick, "AppPush.Install");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPush.Install");
        }
    };

    /** The menu item - "" where this browser cannot receive pushes. Its state is read when the menu is drawn. */
    public static Item = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (kWebPushClient.Supported() === false) { return ""; }
            let On: boolean = false;
            if (typeof Notification !== "undefined" && Notification.permission === "granted" && AppPush.Stored() === true) { On = true; }
            return AppMenuCheck.Html(AppPushRules.Attr, "1", On, AppPushRules.Tip, AppPushRules.Words);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPush.Item");
            return "";
        }
    };

    public static OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (!(Ev.target instanceof Element) || AppPush.Client === null) { return; }
            const Item: Element | null = Ev.target.closest("[" + AppPushRules.Attr + "]");
            if (Item === null) { return; }
            // ⭐ 25 ms: the item says it is working at once; the answer settles it.
            Item.setAttribute("aria-busy", "true");
            const Done: AppPushDone = new AppPushDone(Item);
            if (Item.getAttribute("aria-checked") === "true") { AppPush.Client.Disable(Done.OnOutcome); return; }
            AppPush.Client.Enable(Done.OnOutcome);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPush.OnClick");
        }
    };

    /** The sentence for a kWebPushClient outcome. DOCUMENTED EXCEPTION: pure lookup.
     *  REUSE-JUSTIFIED: "failed" / "denied" are the BROWSER or the service refusing (blocked notifications, no push service,
     *  private mode, a dormant service) - conditions the owner can act on, so they are SHOWN (law 1, fate 1), never reported:
     *  a report would latch the app's gate and stop the page over a browser setting. A fault in our own code still goes to
     *  the handler from every catch here. */
    public static WordsFor = (Outcome: string, Why: string): string => {
        if (Outcome === kWebPushClient.On) { return AppPushRules.OnWords; }
        if (Outcome === kWebPushClient.Denied) { return AppPushRules.DeniedWords; }
        if (Outcome === kWebPushClient.Failed && Why === AppPushRules.NotYetWords) { return Why; }
        if (Outcome === kWebPushClient.Failed) { return AppPushRules.FailedWords; }
        return AppPushRules.OffWords;
    };

    /** This browser's memory that it subscribed ("wac.push" = "1"). ERROR-SUPPRESSED-JUSTIFIED: blocked storage = off. */
    public static Stored = (): boolean => {
        try {
            return window.localStorage.getItem("wac.push") === "1";
        } catch (e) {
            return false;
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: blocked storage = the menu re-reads off next time; the subscription itself stands. */
    public static Remember = (On: boolean): void => {
        try {
            if (On === true) { window.localStorage.setItem("wac.push", "1"); return; }
            window.localStorage.removeItem("wac.push");
        } catch (e) {
            return;
        }
    };
}

/** One click's outcome: the item's state and one sentence. */
class AppPushDone {
    constructor(private readonly Item: Element) { }

    // REUSE-JUSTIFIED: shows the outcome under the item - a refusal is the browser's / service's answer (see `WordsFor`).
    public OnOutcome = (Outcome: string, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const On: boolean = Outcome === kWebPushClient.On;
            AppPush.Remember(On);
            this.Item.removeAttribute("aria-busy");
            this.Item.setAttribute("aria-checked", String(On));
            // The outcome in words, under the item's own words (a status region, so a screen reader hears it once).
            let Note: Element | null = this.Item.querySelector(".menunote");
            if (Note === null) {
                Note = this.Item.ownerDocument.createElement("span");
                Note.className = "menunote";
                Note.setAttribute("role", "status");
                this.Item.appendChild(Note);
            }
            Note.textContent = AppPush.WordsFor(Outcome, Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPushDone.OnOutcome");
        }
    };
}
